import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/toaster';

// Eager load HomePage for fast initial render
import HomePage from './pages/HomePage';

// Lazy load other pages for better performance
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const PropertyDetailsPage = lazy(() => import('./pages/PropertyDetailsPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PropertiesManagement = lazy(() => import('./pages/PropertiesManagement'));
const MessagesManagement = lazy(() => import('./pages/MessagesManagement'));
const SettingsManagement = lazy(() => import('./pages/SettingsManagement'));
const BrandingModule = lazy(() => import('./pages/admin/BrandingModule'));
const LayoutModule = lazy(() => import('./pages/admin/LayoutModule'));
const NavigationModule = lazy(() => import('./pages/admin/NavigationModule'));
const SEOModule = lazy(() => import('./pages/admin/SEOModule'));
const AnalyticsModule = lazy(() => import('./pages/admin/AnalyticsModule'));
const FormsModule = lazy(() => import('./pages/admin/FormsModule'));
const PerformanceModule = lazy(() => import('./pages/admin/PerformanceModule'));
const IntegrationsModule = lazy(() => import('./pages/admin/IntegrationsModule'));
const SecurityModule = lazy(() => import('./pages/admin/SecurityModule'));
const NotificationsModule = lazy(() => import('./pages/admin/NotificationsModule'));
const LocalizationModule = lazy(() => import('./pages/admin/LocalizationModule'));
const BlogModule = lazy(() => import('./pages/admin/BlogModule'));
const PagesModule = lazy(() => import('./pages/admin/PagesModule'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A038]"></div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/properties" element={<PropertiesPage />} />
              <Route path="/property/:id" element={<PropertyDetailsPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:id" element={<BlogPostPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/properties" element={<PropertiesManagement />} />
              <Route path="/admin/messages" element={<MessagesManagement />} />
              <Route path="/admin/settings" element={<SettingsManagement />} />
              <Route path="/admin/branding" element={<BrandingModule />} />
              <Route path="/admin/layout" element={<LayoutModule />} />
              <Route path="/admin/navigation" element={<NavigationModule />} />
              <Route path="/admin/seo" element={<SEOModule />} />
              <Route path="/admin/analytics" element={<AnalyticsModule />} />
              <Route path="/admin/forms" element={<FormsModule />} />
              <Route path="/admin/performance" element={<PerformanceModule />} />
              <Route path="/admin/integrations" element={<IntegrationsModule />} />
              <Route path="/admin/security" element={<SecurityModule />} />
              <Route path="/admin/notifications" element={<NotificationsModule />} />
              <Route path="/admin/localization" element={<LocalizationModule />} />
              <Route path="/admin/blog" element={<BlogModule />} />
              <Route path="/admin/pages" element={<PagesModule />} />
            </Routes>
          </Suspense>
          <Toaster />
        </BrowserRouter>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
